import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut, Home, Settings, Menu, X, Users, MessageCircle } from 'lucide-react';

interface UserNavbarProps {
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    userSource?: string;
  };
  onLogout: () => void;
  onInviteColleagues?: () => void;
  onOpenFeedback?: () => void;
}

export function UserNavbar({ user, onLogout, onInviteColleagues, onOpenFeedback }: UserNavbarProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState('/logo-gold-small.png');
  
  // Hide RESOURCES for directly invited users (User A)
  useEffect(() => {
    const updateLogo = () => {
      if (window.innerWidth <= 768) {
        // Mobile: recberry_gold_r.png
        setLogoSrc('/recberry_gold_r.png');
      } else {
        // Desktop/Tablet: logo-gold-small.png
        setLogoSrc('/logo-gold-small.png');
      }
    };

    updateLogo();
    window.addEventListener('resize', updateLogo);
    return () => window.removeEventListener('resize', updateLogo);
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <style>{styles}</style>
      <nav id="navbar">
        <div className="navbar-container">
          <div className="logo">
            <Link to={`/user/${user.id}/dashboard`}>
              <img 
                src={logoSrc} 
                alt="recberry" 
                className="logo-image"
              />
            </Link>
          </div>
          
          {/* All Navigation Options - Aligned to Right */}
          <div className="nav-options desktop-menu">
            <Link 
              to={`/user/${user.id}/dashboard`}
              className="dashboard-link"
              onClick={closeMobileMenu}
            >
              <Home size={16} />
              DASHBOARD
            </Link>
            <Link 
              to={`/user/${user.id}/account`}
              className={location.pathname === `/user/${user.id}/account` ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              <Settings size={16} />
              ACCOUNT
            </Link>
            {onInviteColleagues && (
              <button 
                onClick={() => {
                  onInviteColleagues();
                  closeMobileMenu();
                }}
                className="invite-colleagues-btn"
              >
                <Users size={16} />
                INVITE COLLEAGUES
              </button>
            )}
            {user.role !== 'admin' && (
              <button
                onClick={() => {
                  if (onOpenFeedback) {
                    onOpenFeedback();
                  }
                  closeMobileMenu();
                }}
                className="feedback-btn"
              >
                <MessageCircle size={16} />
                SHARE FEEDBACK
              </button>
            )}
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={16} />
              LOG OUT
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
          <div className="mobile-menu-content">
            <div className="mobile-user-info">
              <User size={20} />
              <span>{user.fullName}</span>
            </div>
            
            <div className="mobile-menu-links">
              <Link 
                to={`/user/${user.id}/dashboard`}
                className={location.pathname === `/user/${user.id}/dashboard` ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                <Home size={20} />
                DASHBOARD
              </Link>
              <Link 
                to={`/user/${user.id}/account`}
                className={location.pathname === `/user/${user.id}/account` ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                <Settings size={20} />
                ACCOUNT
              </Link>
              {onInviteColleagues && (
                <button 
                  onClick={() => {
                    onInviteColleagues();
                    closeMobileMenu();
                  }}
                  className="mobile-invite-colleagues-btn"
                >
                  <Users size={20} />
                  INVITE COLLEAGUES
                </button>
              )}
              {user.role !== 'admin' && (
                <button
                  onClick={() => {
                    if (onOpenFeedback) {
                      onOpenFeedback();
                    }
                    closeMobileMenu();
                  }}
                  className="mobile-feedback-btn"
                >
                  <MessageCircle size={20} />
                  SHARE FEEDBACK
                </button>
              )}
            </div>
            
            <button onClick={handleLogout} className="mobile-logout-btn">
              <LogOut size={20} />
              LOG OUT
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}

const styles = `
  #navbar {
    background: var(--dark);
    border-bottom: 1px solid var(--orange);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    width: 100%;
  }

  .navbar-container {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 80px;
    width: 100%;
    box-sizing: border-box;
    position: relative;
  }

  .logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-right: 2rem;
  }

  .logo-image {
    max-width: 180px;
    max-height: 180px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }

  .nav-options {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: nowrap;
    min-width: 0;
    flex-shrink: 1;
  }

  .dashboard-link {
    color: white;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    transition: color 0.2s ease;
    background: transparent;
    border: none;
  }

  .dashboard-link:hover {
    color: var(--orange);
  }

  .nav-options a:not(.dashboard-link) {
    color: white;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }

  .nav-options a:not(.dashboard-link):hover {
    background: var(--orange);
    color: var(--dark);
    border: 1px solid var(--orange);
  }

  .nav-options a:not(.dashboard-link).active {
    background: var(--orange);
    color: var(--dark);
    border: 1px solid var(--orange);
  }

  .user-menu {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--light);
    font-size: 0.875rem;
    white-space: nowrap;
  }

  .logout-btn {
    background: transparent;
    border: 1px solid var(--orange);
    color: var(--orange);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .logout-btn:hover {
    background: var(--orange);
    color: var(--dark);
  }

  .invite-colleagues-btn {
    background: var(--orange);
    border: 1px solid var(--orange);
    color: var(--dark);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
    font-weight: 500;
    white-space: nowrap;
  }

  .invite-colleagues-btn:hover {
    background: var(--dark-blue);
    color: white;
    border: 1px solid var(--dark-blue);
  }

  .feedback-btn {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
    font-weight: 500;
    white-space: nowrap;
  }

  .feedback-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--orange);
    border-color: var(--orange);
  }

  /* Mobile Menu Toggle Button */
  .mobile-menu-toggle {
    display: none;
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    z-index: 1001;
  }

  .mobile-menu-toggle:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  /* Mobile Menu Overlay */
  .mobile-menu {
    display: none;
    position: fixed;
    top: 80px;
    right: 0;
    width: 280px;
    height: calc(100vh - 80px);
    background: var(--dark);
    z-index: 999;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
  }

  .mobile-menu.mobile-menu-open {
    transform: translateX(0);
  }

  .mobile-menu-content {
    padding: 2rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    height: 100%;
  }

  .mobile-user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: white;
    font-size: 1rem;
    font-weight: 500;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
  }

  .mobile-menu-links {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .mobile-menu-links a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: white;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 500;
    padding: 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .mobile-menu-links a:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .mobile-menu-links a.active {
    background: var(--orange);
    color: var(--dark);
  }

  .mobile-logout-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: var(--orange);
    color: var(--dark);
    border: none;
    padding: 1rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: auto;
  }

  .mobile-logout-btn:hover {
    background: var(--dark-blue);
    color: white;
  }

  .mobile-invite-colleagues-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: var(--orange);
    color: var(--dark);
    border: none;
    padding: 1rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 1rem;
  }

  .mobile-invite-colleagues-btn:hover {
    background: var(--dark-blue);
    color: white;
  }

  .mobile-feedback-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: transparent;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.35);
    padding: 1rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .mobile-feedback-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--orange);
    border-color: var(--orange);
  }

/* Responsive Design */
@media (max-width: 1024px) {
  /* Tablet and Mobile - Show hamburger menu */
  .navbar-container {
    padding: 0 15px;
  }
  
  /* Hide desktop menu on tablet and mobile */
  .nav-options {
    display: none;
  }
  
  /* Show mobile hamburger button */
  .mobile-menu-toggle {
    display: block;
  }
  
  /* Show mobile menu */
  .mobile-menu {
    display: block;
    top: 80px;
    height: calc(100vh - 80px);
  }
}

@media (max-width: 768px) {
  .navbar-container {
    height: 80px;
  }
  
  .logo-image {
    height: 28px;
  }
}
`;
